import React, { useState } from "react";
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import {
    Box,
    Button,
    FormControl,
    Grid,
    Typography,
} from "@mui/material";

const ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: "16px",
            color: "#000",
            "::placeholder": { color: "#999" },
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            padding: "10px 14px",
        },
        invalid: { color: "#f44336" },
    },
};

const CheckoutForm = ({
    amount,
    parentName,
    email,
    paymentMethod,
    studentDetails = {},
}) => {
    const stripe = useStripe();
    const elements = useElements();

    const [message, setMessage] = useState("");
    const [hasSubmitted, setSubmitted] = useState(false);      // NEW
    const [processing, setProcessing] = useState(false);      // NEW

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (hasSubmitted) return;                                 // NEW - guard
        setProcessing(true);                                      // NEW
        setMessage("");

        try {
            if (!stripe || !elements) {
                setMessage("Stripe.js has not loaded yet.");
                setProcessing(false);                                 // NEW
                return;
            }

            const { data } = await axios.post(
                "https://figazuresql.swancreate.com/api/swa/create-payment-intent",
                {
                    amount: parseInt(amount) * 100,
                    name: parentName,
                    email,
                    paymentMethodType: paymentMethod,
                }
            );

            const clientSecret = data.clientSecret;

            if (paymentMethod === "card") {
                const result = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: {
                        card: elements.getElement(CardNumberElement),
                        billing_details: { name: parentName, email },
                    },
                });

                if (result.error) setMessage(result.error.message);
                else if (result.paymentIntent.status === "succeeded") {
                    await addPaymentRecord(result.paymentIntent.id, "Succeeded");
                    setMessage("✅ Payment successful!");
                }
            } else if (paymentMethod === "us_bank_account") {
                const collect = await stripe.collectBankAccountForPayment({
                    clientSecret,
                    params: {
                        payment_method_type: "us_bank_account",
                        payment_method_data: {
                            billing_details: { name: parentName, email },
                        },
                    },
                });

                if (collect.error) { setMessage(collect.error.message); }
                else {
                    const confirm = await stripe.confirmUsBankAccountPayment(clientSecret);
                    const status = confirm.paymentIntent?.status;
                    if (confirm.error) setMessage(confirm.error.message);
                    else {
                        await addPaymentRecord(confirm.paymentIntent.id, status);
                        setMessage(
                            status === "processing"
                                ? "✅ Bank payment initiated! It may take a few days."
                                : "✅ Bank payment successful!"
                        );
                    }
                }
            }

            setSubmitted(true);                                     // NEW – lock form
        } catch (err) {
            console.error("Payment error:", err);
            setMessage("An error occurred while processing the payment.");
        } finally {
            setProcessing(false);                                   // NEW
        }
    };

    const addPaymentRecord = async (paymentReference, status) => {
        try {
            await axios.post("https://figazuresql.swancreate.com/api/swa/payments", {
                StudentGuidID: studentDetails.StudentguidID,
                SchoolDistrictId: studentDetails.SchoolDistrictId,
                guardian_FName: studentDetails.guardian_FName,
                guardian_LName: studentDetails.guardian_LName,
                PaymentEmail: email,
                StudentFName: studentDetails.FirstName,
                StudentLName: studentDetails.LastName,
                SchoolId: studentDetails.SiteID,
                SchoolName: studentDetails.SchoolName,
                PaymentAmount: amount,
                PaymentReference: paymentReference,
                StripePayoutReference: paymentReference,
                PaymentDate: new Date().toISOString(),
                isPaid: status === "Succeeded",
                PaymentSource: "Stripe",
            });
        } catch (error) {
            console.error("Error adding payment record:", error);
            setMessage("Failed to record payment details.");
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mx: "auto" }}>
            {paymentMethod === "card" && (
                <>
                    <FormControl fullWidth margin="normal">
                        <Box
                            sx={{
                                padding: "10.5px 14px",
                                border: "1px solid #ccc",
                                borderRadius: 1,
                                "& .StripeElement": { padding: 0 },
                            }}
                        >
                            <CardNumberElement options={ELEMENT_OPTIONS} />
                        </Box>
                    </FormControl>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <FormControl fullWidth margin="normal">
                                <Box
                                    sx={{
                                        padding: "10.5px 14px",
                                        border: "1px solid #ccc",
                                        borderRadius: 1,
                                    }}
                                >
                                    <CardExpiryElement options={ELEMENT_OPTIONS} />
                                </Box>
                            </FormControl>
                        </Grid>
                        <Grid item xs={6}>
                            <FormControl fullWidth margin="normal">
                                <Box
                                    sx={{
                                        padding: "10.5px 14px",
                                        border: "1px solid #ccc",
                                        borderRadius: 1,
                                    }}
                                >
                                    <CardCvcElement options={ELEMENT_OPTIONS} />
                                </Box>
                            </FormControl>
                        </Grid>
                    </Grid>
                </>
            )}

            {paymentMethod === "us_bank_account" && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                    You'll be asked to securely link your bank account. This is handled by Stripe.
                </Typography>
            )}

            <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={!stripe || processing || hasSubmitted}     /* NEW */
                fullWidth
                sx={{ mt: 2 }}
            >
                {processing ? "Processing…" : hasSubmitted ? "Paid" : "Pay"}
            </Button>

            {message && (
                <Typography
                    variant="body2"
                    color={message.includes("✅") ? "success.main" : "error.main"}
                    sx={{ mt: 2 }}
                >
                    {message}
                </Typography>
            )}
        </Box>
    );
};

export default CheckoutForm;
